import { action, observable, makeObservable } from "mobx";

import { Customer, CustomerRequestBody } from "../models/Customer/Customer";
import CustomerAPI from "../api/CustomerAPI";
import { ActivityLog } from "../models/Activity/ActivityLog";
import { EmployeeRoles } from "../models/Employees/EmployeeRoles";
import { GetCustomerParams } from "../models/Customer/getCustomerParams";

export class CustomerStore {
  customers: Customer[];
  customerAPI: CustomerAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  customerDetails: Customer;
  originalCustomer: Customer;
  permissionMap: Map<string, string>;
  activityLogs: ActivityLog[];

  constructor() {
    this.customers = [];
    this.customerAPI = new CustomerAPI();
    this.permissionMap = new Map<string, string>();
    this.customerDetails = {};
    this.originalCustomer = {};
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.activityLogs = [];

    makeObservable(this, {
      customers: observable,
      customerDetails: observable,
      originalCustomer: observable,
      updateName: action,
      updateLegalName: action,
      getCustomers: action,
      discardChanges: action,
      searchCustomers: action,
      updateCustomerDetails: action,
      updateRedemptionMail: action,
      clearCustomerDetails: action,
      updateLocalCustomerDetails: action,
      updateEmailFrom: action,
      updateUrl: action,
      updateRedemptionEmail: action,
      updateEnableCoupon: action,
      updateEnableRedemptionEmail: action,
      updateEnableInventory: action,
      updateEnableGOC: action,
      updateEnableCampaign: action,
      updateEnableShopify: action,
      createCustomer: action,
      updateEnableSez: action,
      updateZohoId: action,
      updateHrOrderId: action,
      updateMarketingOrderId: action,
      updateGoldstandardOrderId: action,
      activityLogs: observable,
      getCustomerActivityLogs: action,
      clearActivityLogs: action,
    });
  }

  searchCustomers = async (filter: GetCustomerParams) => {
    try {
      const response = await this.customerAPI.getCustomers(filter);
      this.customers = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  hasPermission = (role: EmployeeRoles | [EmployeeRoles]): boolean => {
    let hasAccess = false;

    if (this.permissionMap.size === 0) return hasAccess;
    if (this.permissionMap.get(EmployeeRoles.ALL)) return true;

    if (Array.isArray(role)) {
      for (let i in role) {
        const item = role[i];
        const permission = this.permissionMap.get(item);
        if (permission) {
          hasAccess = true;
          break;
        }
      }
    } else {
      const permission = this.permissionMap.get(role);
      if (permission) hasAccess = true;
    }

    return hasAccess;
  };

  clearCustomerDetails = () => {
    this.originalCustomer = {};
    this.customerDetails = {};
  };

  getCustomers = async (filter: GetCustomerParams) => {
    try {
      const response = await this.customerAPI.getCustomers(filter);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.customers = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  reset = () => {
    this.customers = [];
    this.customerDetails = {} as Customer;
    this.originalCustomer = {} as Customer;
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
  };

  updateCustomerDetails = async () => {
    try {
      if (!this.customerDetails.id) return;

      const customerRequest = {
        id: this.customerDetails.id,
        name: this.customerDetails.name,
        legal_name: this.customerDetails.legal_name,
        redemption_url: this.customerDetails.config?.redemption_url,
        redemption_email: this.customerDetails.config?.email,
        redemption_email_from: this.customerDetails.config?.from_email_name,
        enable_coupon_redemption:
          this.customerDetails.config?.coupon_redemption_enabled,
        enable_email_redemption:
          this.customerDetails.config?.send_redemption_email,
        enable_inventory: this.customerDetails.config?.inventory_enabled,
        enable_goc: this.customerDetails.config?.gift_of_choice_enabled,
        enable_campaigns: this.customerDetails.config?.campaigns_enabled,
        enable_swagstore: this.customerDetails.config?.swagstore_enabled,
        enable_shopify: this.customerDetails.config?.shopify_enabled,
        is_sez: this.customerDetails.config?.is_sez,
        inventory_type: this.customerDetails.config?.inventory_type,
        zoho_id: this.customerDetails.zoho_id,
        hr_order_id: this.customerDetails.hr_order_id,
        marketing_order_id: this.customerDetails.marketing_order_id,
        goldstandard_order_id: this.customerDetails.goldstandard_order_id,
      };

      const response =
        await this.customerAPI.updateCustomerDetails(customerRequest);

      this.updateLocalCustomerDetails(response.result);
      return response.result;
    } catch (error: any) {
      throw error;
    }
  };

  updateName = (name: string) => {
    this.customerDetails.name = name;
  };

  updateLegalName = (legalName: string) => {
    this.customerDetails.legal_name = legalName;
  };

  updateRedemptionMail = (redemptionMail: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      redemption_email: redemptionMail,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEmailFrom = (emailFrom: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      from_email_name: emailFrom,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateZohoId = (zohoId: string) => {
    this.customerDetails.zoho_id = zohoId;
  };

  updateHrOrderId = (hrOrderId: string) => {
    this.customerDetails.hr_order_id = hrOrderId;
  };

  updateMarketingOrderId = (marketingOrderId: string) => {
    this.customerDetails.marketing_order_id = marketingOrderId;
  };

  updateGoldstandardOrderId = (goldstandardOrderId: string) => {
    this.customerDetails.goldstandard_order_id = goldstandardOrderId;
  };

  updateRedemptionEmail = (redemptionMail: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      email: redemptionMail,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateUrl = (redemptionUrl: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      redemption_url: redemptionUrl,
    };
    this.customerDetails.config = updatedConfig;
  };

  updateEnableCoupon = (enableCoupon: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      coupon_redemption_enabled: enableCoupon,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateInventoryType = (inventoryType: any) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      inventory_type: inventoryType,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableRedemptionEmail = (redemptionMail: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      send_redemption_email: redemptionMail,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableInventory = (inventory: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      inventory_enabled: inventory,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableGOC = (goc: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      gift_of_choice_enabled: goc,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableCampaign = (campaign: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      campaigns_enabled: campaign,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableSwagStore = (swagstore: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      swagstore_enabled: swagstore,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableShopify = (shopify: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      shopify_enabled: shopify,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableSez = (sez: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      is_sez: sez,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateLocalCustomerDetails = (customer: Customer) => {
    this.customerDetails = customer;
    this.originalCustomer = customer;
  };

  getCustomerDetailsById = async (customerId: number | null) => {
    try {
      const response = await this.customerAPI.getCustomerDetailsById(
        customerId!,
      );
      this.updateLocalCustomerDetails(response);
    } catch (error: any) {
      throw error;
    }
  };

  discardChanges = () => {
    this.customerDetails = { ...this.originalCustomer };
  };

  createCustomer = async (customerDetails: CustomerRequestBody) => {
    try {
      const customerRequest: CustomerRequestBody = {
        id: this.customerDetails.id,
        name: this.customerDetails.name ?? "",
        legal_name: this.customerDetails.legal_name ?? "",
        redemption_url: this.customerDetails.config?.redemption_url ?? "",
        redemption_email: this.customerDetails.config?.email ?? "",
        redemption_email_from:
          this.customerDetails.config?.from_email_name ?? "",
        enable_coupon_redemption:
          this.customerDetails.config?.coupon_redemption_enabled ?? false,
        enable_email_redemption:
          this.customerDetails.config?.send_redemption_email ?? false,
        enable_inventory:
          this.customerDetails.config?.inventory_enabled ?? false,
        enable_goc:
          this.customerDetails.config?.gift_of_choice_enabled ?? false,
        enable_campaigns:
          this.customerDetails.config?.campaigns_enabled ?? false,
        enable_swagstore:
          this.customerDetails.config?.swagstore_enabled ?? false,
        enable_shopify: this.customerDetails.config?.shopify_enabled ?? false,
        is_sez: this.customerDetails.config?.is_sez ?? false,
        inventory_type: this.customerDetails.config?.inventory_type,
        hr_order_id: this.customerDetails.hr_order_id ?? "",
        marketing_order_id: this.customerDetails.marketing_order_id ?? "",
        goldstandard_order_id: this.customerDetails.goldstandard_order_id ?? "",
      };

      const response = await this.customerAPI.createCustomer(customerRequest);

      this.customerDetails = response.result || [];
      return response;
    } catch (error: any) {
      console.error("Error creating customer:", error);
      throw error;
    }
  };

  deleteCustomer = async (customerId: number) => {
    try {
      const response = await this.customerAPI.deleteCustomer(customerId);
      if (response.success && this.customerDetails) {
        const index = this.customers.findIndex(
          (item) => item.id === customerId,
        );
        if (index !== -1) {
          this.customers[index] = {
            ...this.customers[index],
          };
        }
      }
      return response;
    } catch (error: any) {
      console.error("Error creating customer:", error);
      throw error;
    }
  };

  getCustomerActivityLogs = async (customerId: number) => {
    try {
      const response =
        await this.customerAPI.getCustomerActivityLogs(customerId);
      this.activityLogs = response;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  clearActivityLogs = () => {
    this.activityLogs = [];
  };
}
